"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/auth.context";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Loader2 } from "lucide-react";
import handleUpload from "@/utils/handleUpload";
import { useRef, useState } from "react";
import getFileUrl from "@/utils/getFileUrl";
import { useTranslation } from "react-i18next";
import { useTheme } from "next-themes";
import { Monitor, Moon, Sun } from "lucide-react";

export default function AccountSettings() {
  const { t } = useTranslation("accountSettings");
  const { user } = useAuth();
  const { theme, setTheme } = useTheme();

  const personalInfoSchema = z.object({
    profilePicture: z.instanceof(File).optional(),
    first_name: z.string().min(2, {
      message: t("validation.firstNameMin"),
    }),
    last_name: z.string().min(2, {
      message: t("validation.lastNameMin"),
    }),
    email: z.string().email({
      message: t("validation.emailInvalid"),
    }),
    bio: z
      .string()
      .max(500, {
        message: t("validation.bioMax"),
      })
      .optional(),
  });

  const passwordSchema = z
    .object({
      currentPassword: z.string().min(6, {
        message: t("validation.passwordMin"),
      }),
      newPassword: z.string().min(6, {
        message: t("validation.passwordMin"),
      }),
      confirmPassword: z.string(),
    })
    .refine((data) => data.newPassword === data.confirmPassword, {
      message: t("validation.passwordsMismatch"),
      path: ["confirmPassword"],
    });

  // Personal info form
  const personalInfoForm = useForm<z.infer<typeof personalInfoSchema>>({
    resolver: zodResolver(personalInfoSchema),
    values: {
      first_name: user?.first_name,
      last_name: user?.last_name,
      email: user?.email,
    },
  });

  // Password form
  const passwordForm = useForm<z.infer<typeof passwordSchema>>({
    resolver: zodResolver(passwordSchema),
    values: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  });

  const { loaderUser } = useAuth();

  async function onPersonalInfoSubmit(
    values: z.infer<typeof personalInfoSchema>
  ) {
    try {
      await api.post("/auth/update-profile", {
        first_name: values.first_name,
        last_name: values.last_name,
      });
      toast.success(t("messages.personalInfoUpdated"));
      loaderUser();
    } catch (error) {
      console.log(error);
      toast.error(t("messages.personalInfoUpdateError"));
    }
  }

  async function onPasswordSubmit(values: z.infer<typeof passwordSchema>) {
    try {
      await api.post("/auth/change-password", values);
      toast.success(t("messages.passwordUpdated"));
      loaderUser();
    } catch (error) {
      console.log(error);
      toast.error(t("messages.passwordUpdateError"));
      const errors = error?.response?.data?.meta?.errors || {};
      Object.keys(errors)?.forEach((field: any) => {
        passwordForm.setError(field, {
          message: errors[field],
        });
      });
    }
  }

  const [uploading, setUploading] = useState(false);

  const handleLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      setUploading(true);
      const file = e.target.files?.[0];
      const { fileUrl }: any = await handleUpload(file);
      await api.post("/auth/update-profile", { profileUrl: fileUrl });
      toast.success(t("messages.logoUpdated"));
      loaderUser();
      setUploading(false);
    } catch (error) {
      toast.error(
        error?.response?.data?.message || t("messages.logoUpdateError"),
      );
      setUploading(false);
    }
  };

  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <div className="md:p-3">
      <div className="container max-w-3xl space-y-4">
        <div className="mb-3">
          <h1 className="text-base font-semibold tracking-tight">
            {t("page.title")}
          </h1>
          <p className="text-muted-foreground mt-0 text-sm">
            {t("page.description")}
          </p>
        </div>

        {/* Personal Information Card */}
        <Form {...personalInfoForm}>
          <form onSubmit={personalInfoForm.handleSubmit(onPersonalInfoSubmit)}>
            <Card className="rounded-md shadow-none">
              <CardHeader>
                <CardTitle className="mb-1">
                  {t("personal.title")}
                </CardTitle>
                <CardDescription className="text-sm mt-2">
                  {t("personal.description")}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex flex-col sm:flex-row gap-6 items-start sm:items-center">
                  <Avatar className="w-20 h-20">
                    <AvatarImage
                      src={getFileUrl(user?.profileUrl)}
                      alt={t("personal.profileAlt")}
                    />
                    <AvatarFallback>
                      {user?.first_name?.[0] + user?.last_name?.[0]}
                    </AvatarFallback>
                  </Avatar>

                  <div className="space-y-2">
                    <Button
                      onClick={(e) => {
                        e.preventDefault();
                        inputRef.current?.click();
                      }}
                      disabled={uploading}
                      size="sm"
                    >
                      {uploading ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        ""
                      )}
                      {t("personal.uploadButton")}
                    </Button>
                    <Input
                      type="file"
                      className="hidden"
                      accept="image/*"
                      onChange={handleLogoChange}
                      ref={inputRef}
                    />

                    <p className="text-xs text-muted-foreground">
                      {t("personal.uploadHint")}
                    </p>
                  </div>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <FormField
                    control={personalInfoForm.control}
                    name="first_name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("personal.fields.firstName")}</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={personalInfoForm.control}
                    name="last_name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("personal.fields.lastName")}</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={personalInfoForm.control}
                  name="email"
                  disabled
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("personal.fields.email")}</FormLabel>
                      <FormControl>
                        <Input type="email" {...field} />
                      </FormControl>
                      <p className="text-xs text-muted-foreground">
                        {t("personal.emailHint")}
                      </p>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
              <CardFooter className="flex justify-end gap-2 border-t px-4 py-2">
                <Button size="sm" variant="outline" type="button">
                  {t("buttons.cancel")}
                </Button>
                <Button
                  disabled={personalInfoForm.formState.isSubmitting}
                  size="sm"
                  type="submit"
                >
                  {personalInfoForm.formState.isSubmitting && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  {t("buttons.saveChanges")}
                </Button>
              </CardFooter>
            </Card>
          </form>
        </Form>

        {/* Change Password Card */}
        <Form {...passwordForm}>
          <form onSubmit={passwordForm.handleSubmit(onPasswordSubmit)}>
            <Card className="rounded-md shadow-none mt-4">
              <CardHeader>
                <CardTitle>{t("password.title")}</CardTitle>
                <CardDescription className="!mt-2">
                  {t("password.description")}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <FormField
                  control={passwordForm.control}
                  name="currentPassword"
                  render={({ field, fieldState }) => (
                    <FormItem>
                      <FormLabel>{t("password.fields.current")}</FormLabel>
                      <FormControl>
                        <Input
                          placeholder={t("password.placeholders.current")}
                          type="password"
                          error={fieldState?.error?.message}
                          {...field}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={passwordForm.control}
                  name="newPassword"
                  render={({ field, fieldState }) => (
                    <FormItem>
                      <FormLabel>{t("password.fields.new")}</FormLabel>
                      <FormControl>
                        <Input
                          placeholder={t("password.placeholders.new")}
                          type="password"
                          error={fieldState?.error?.message}
                          {...field}
                        />
                      </FormControl>
                      <p className="text-xs text-muted-foreground">
                        {t("password.hint")}
                      </p>
                    </FormItem>
                  )}
                />
                <FormField
                  control={passwordForm.control}
                  name="confirmPassword"
                  render={({ field, fieldState }) => (
                    <FormItem>
                      <FormLabel>{t("password.fields.confirm")}</FormLabel>
                      <FormControl>
                        <Input
                          placeholder={t("password.placeholders.confirm")}
                          type="password"
                          error={fieldState?.error?.message}
                          {...field}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </CardContent>
              <CardFooter className="flex justify-end border-t px-4 py-2">
                <Button
                  disabled={passwordForm.formState.isSubmitting}
                  size="sm"
                  type="submit"
                >
                  {passwordForm.formState.isSubmitting && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  {t("password.updateButton")}
                </Button>
              </CardFooter>
            </Card>
          </form>
        </Form>
        {/* Appearance Card */}
        <Card className="rounded-md shadow-none mt-4">
          <CardHeader>
            <CardTitle>Appearance</CardTitle>
            <CardDescription className="!mt-2">
              Choose how One Suite looks to you. Select a theme or use your system default.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-3">
              {[
                { value: "light", label: "Light", Icon: Sun },
                { value: "dark", label: "Dark", Icon: Moon },
                { value: "system", label: "System", Icon: Monitor },
              ].map(({ value, label, Icon }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setTheme(value)}
                  className={`flex flex-col items-center gap-2 rounded-md border-2 p-4 transition-colors hover:bg-accent cursor-pointer ${
                    theme === value
                      ? "border-primary bg-primary/5"
                      : "border-border"
                  }`}
                >
                  <Icon
                    className={`size-5 ${
                      theme === value ? "text-primary" : "text-muted-foreground"
                    }`}
                  />
                  <span
                    className={`text-sm font-medium ${
                      theme === value ? "text-primary" : "text-foreground"
                    }`}
                  >
                    {label}
                  </span>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
